import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { Prompt } from 'react-router-dom';

import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { bindActionCreators } from 'redux';

import {
    Typography, IconButton, ListItem, ListItemText, List, TextField, Button,
} from '@material-ui/core';

import {
    validateEmail, validateRequired, validatePassword, validatePasswordConfirm, validateAllFields, validateField,
} from '../../youwell-common/validationUtils';
import * as AuthStore from '../../app/store/AuthStore';
import { hasValue } from '../../youwell-common/objectUtils';
import Auth from '../../youwell-common/Auth';
import { loginLevels } from '../../youwell-common/constants';
import { CancelIcon, SaveIcon, EditIcon } from '../../common/icons';
import { timestamp } from '../../youwell-common/dateUtils';

const styles = theme => ({
    root: {
        padding: theme.spacing(1),
    },
    details: {
        display: 'flex',
        padding: theme.spacing(2),
    },
    imageContainer: {
        margin: theme.spacing(1),
    },
    imageField: {
        background: theme.palette.common.white,
    },
    textFields: {
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
    },
    row: {
        display: 'flex',
        alignItems: 'center',
    },
    field: {
        margin: theme.spacing(1),
        flexGrow: 1,
    },
    actionContainer: {
        margin: theme.spacing(1),
        marginLeft: -theme.spacing(1),
        border: '2px solid transparent',
        background: theme.palette.common.white,
    },
    infoMessage: {
        flex: '1 1 auto',
        margin: theme.spacing(1),
        padding: theme.spacing(2),
        background: theme.palette.common.white,
        border: theme.borders.mediumSecondary,
        borderColor: theme.colors.blue,
        color: theme.colors.blue,
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        margin: `${theme.spacing(2)}px ${theme.spacing(1)}px`,
    },
    button: {
        marginLeft: theme.spacing(1),
    },
    buttonIcon: {
        marginRight: theme.spacing(1),
    },
    section: {
        marginTop: theme.spacing(2),
        background: theme.palette.common.white,
        padding: theme.spacing(2),
    },
    sectionHeader: {
        marginBottom: theme.spacing(1),
    },
    roleText: {
        display: 'flex',
    },
    roleOrganization: {
        marginRight: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
    },
});

const defaultState = {
    name: '',
    email: '',
    phone: '',
    newPassword: '',
    newPasswordConfirm: '',
    imageFileId: '',
    userSettings: {},
};

const validators = {
    name: validateRequired,
    email: validateEmail,
    newPassword: validatePassword,
    newPasswordConfirm: validatePasswordConfirm,
};

const stateFromData = (data) => {
    const initialState = {
        ...defaultState,
        hasUnsavedChanges: false,
        editing: false,
        validationErrors: {},
        id: data ? data.id : null,
    };

    if (!data) {
        return initialState;
    }

    return Object.keys(defaultState).reduce(
        (obj, key) => (hasValue(data[key]) ? { ...obj, [key]: data[key] } : { ...obj }),
        {
            ...initialState,
        },
    );
};


class Profile extends React.Component {
    state = stateFromData(this.props.userProfile);


    componentDidMount() {
        this.props.getAllTrustedDevicesForUser();
    }

    static getDerivedStateFromProps(props, state) {
        if (props.userProfile && !state.id) {
            return stateFromData(props.userProfile);
        }
        return null;
    }

    handleChange = name => (event) => {
        this.setState({ [name]: event.target.value, hasUnsavedChanges: true }, () => {
            if (validators[name]) {
                const validationErrors = validateField(this.state, validators[name], name); // eslint-disable-line react/no-access-state-in-setstate
                this.setState({ validationErrors });
            }
        });
    }

    handleConfigChange = name => (event) => {
        this.setState(prevState => ({
            editing: true,
            hasUnsavedChanges: true,
            userSettings: {
                ...prevState.userSettings,
                [name]: event.target.value,
            },
        }));
    }

    startEdit = () => {
        this.setState({ editing: true });
    }

    cancel = () => {
        this.setState(stateFromData(this.props.userProfile));
    }

    saveChanges = () => {
        const validationErrors = validateAllFields(this.state, validators);

        if (!validationErrors.hasErrors) {
            const saveData = Object.keys(defaultState).reduce(
                (obj, key) => (this.state[key] ? { ...obj, [key]: this.state[key] } : { ...obj }),
                {
                    id: this.props.userProfile.id,
                },
            );

            this.props.saveUserProfile(saveData, { callback: this.setState({ editing: false, hasUnsavedChanges: false }) });
        } else {
            this.setState({ validationErrors });
        }
    }

    handleRemoveTrustedDevicesOK = () => {
        this.props.removeAllTrustedDevices();
    }

    render() {
        const {
            classes, translate, userProfile, trustedDevices,
        } = this.props;

        if (!userProfile) {
            return null;
        }

        const loginLevel = Auth.getLoginLevel();
        const isLoggedInLevel4 = loginLevel === loginLevels.level4;
        const readOnly = !this.state.editing;

        return (
            <div className={classes.root}>
                <Prompt
                    when={this.state.hasUnsavedChanges}
                    message={translate('navigate_away_unsaved_changes')}
                />

                <div className={classes.details}>
                    {/* <div className={classes.imageContainer}>
                        <ImageInput size={250} fileId={this.state.imageFileId} onChange={this.handleChange('imageFileId')} disabled={readOnly} className={classes.imageField} />
                    </div> */}
                    <div className={classes.textFields}>
                        <div className={classes.row}>
                            <TextField
                                id="name"
                                label={translate('profile.name')}
                                value={this.state.name}
                                disabled={readOnly}
                                onChange={this.handleChange('name')}
                                className={classes.field}
                                error={!!this.state.validationErrors.name}
                                helperText={this.state.validationErrors.name ? translate(this.state.validationErrors.name) : null}
                            />
                        </div>

                        <div className={classes.row}>
                            <TextField
                                id="email"
                                label={translate('profile.email')}
                                value={this.state.email}
                                disabled={readOnly}
                                onChange={this.handleChange('email')}
                                className={classes.field}
                                error={!!this.state.validationErrors.email}
                                helperText={this.state.validationErrors.email ? translate(this.state.validationErrors.email) : null}
                            />

                            <TextField
                                id="phone"
                                label={translate('profile.phone')}
                                value={this.state.phone}
                                disabled={readOnly}
                                onChange={this.handleChange('phone')}
                                className={classes.field}
                                error={!!this.state.validationErrors.phone}
                                helperText={this.state.validationErrors.phone ? translate(this.state.validationErrors.phone) : null}
                            />
                        </div>

                        {!isLoggedInLevel4 && (
                            <>
                                <div className={classes.row}>
                                    <TextField
                                        id="newPassword"
                                        label={translate('profile.new_password')}
                                        type="password"
                                        value={this.state.newPassword}
                                        disabled={readOnly}
                                        onChange={this.handleChange('newPassword')}
                                        className={classes.field}
                                        error={!!this.state.validationErrors.newPassword}
                                        helperText={this.state.validationErrors.newPassword ? translate(this.state.validationErrors.newPassword) : null}
                                    />
                                </div>
                                <div className={classes.row}>
                                    <TextField
                                        id="newPasswordConfirm"
                                        label={translate('profile.new_password_confirm')}
                                        type="password"
                                        value={this.state.newPasswordConfirm}
                                        disabled={readOnly}
                                        onChange={this.handleChange('newPasswordConfirm')}
                                        className={classes.field}
                                        error={!!this.state.validationErrors.newPasswordConfirm}
                                        helperText={this.state.validationErrors.newPasswordConfirm ? translate(this.state.validationErrors.newPasswordConfirm) : null}
                                    />
                                </div>
                            </>
                        )}

                        <div className={classes.actions}>
                            {this.state.editing ? (
                                <React.Fragment>
                                    <IconButton onClick={this.cancel} className={classes.iconButton} color="secondary">
                                        <CancelIcon />
                                    </IconButton>
                                    <IconButton onClick={this.saveChanges} className={classes.iconButton} color="primary">
                                        <SaveIcon />
                                    </IconButton>
                                </React.Fragment>
                            ) : (
                                <IconButton onClick={this.startEdit} className={classes.iconButton}>
                                    <EditIcon />
                                </IconButton>
                            )}
                        </div>
                    </div>
                </div>


                {trustedDevices?.length > 0 && (
                    <div className={classes.section}>
                        <Typography variant="subtitle1" className={classes.sectionHeader}>
                            {translate('profile.myTrustedDevices')}
                        </Typography>

                        <List>
                            {trustedDevices.map(device => (
                                <ListItem key={device.id}>
                                    <ListItemText
                                        primary={translate('profile.trustedDeviceCreated', { created: timestamp(device.created, translate) })}
                                        secondary={device.description}
                                    />
                                </ListItem>
                            ))}
                        </List>

                        <div className={classes.actions}>
                            <Button
                                onClick={this.handleRemoveTrustedDevicesOK}
                                size="small"
                            >
                                {translate('profile.removeTrustedDevices')}
                            </Button>
                        </div>
                    </div>
                )}

            </div>
        );
    }
}

Profile.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    userProfile: PropTypes.object.isRequired,
    saveUserProfile: PropTypes.func.isRequired,
    trustedDevices: PropTypes.array.isRequired,
    getAllTrustedDevicesForUser: PropTypes.func.isRequired,
    removeAllTrustedDevices: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
    userProfile: state.auth.profileData,
    trustedDevices: state.auth.trustedDevices,
});

const mapDispatchToProps = dispatch => ({
    ...bindActionCreators(AuthStore.actionCreators, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Profile));
